import { extendVariants, Tabs as HeroUITabs, Tab } from "@heroui/react";
import clsx from "clsx";
import { ComponentProps, ReactNode } from "react";

type TabsVariant = "bordered" | "underlined" | "no-underline";

type ExtendedTabsProps = Omit<ComponentProps<typeof HeroUITabs>, "variant"> & {
  variant?: TabsVariant;
};

export const ExtendedTabs = extendVariants(HeroUITabs, {
  variants: {
    variant: {
      bordered: {
        cursor: "bg-primary shadow-none rounded-md",
        tabContent:
          "body-lg group-data-[selected=true]:bg-primary rounded-md shadow-none",
        tabList: "shadow-none border border-primary rounded-md",
      },
      "no-underline": {
        cursor: "hidden",
        tabContent: "group-data-[selected=true]:text-primary body-lg",
        tabList: "bg-transparent",
      },
      underlined: {
        cursor: "bg-primary shadow-none w-full",
        tabContent:
          "group-data-[selected=true]:text-primary body-lg group-data-[selected=true]:font-bold!",
        tabList: "p-0",
      },
    },
  },
}) as React.FC<ExtendedTabsProps>;

type Item = {
  key: string;
  title: string | ReactNode;
  content: ReactNode;
};

export type TabsProps = Omit<ExtendedTabsProps, "variant" | "items"> & {
  items: Item[];
  variant: TabsVariant;
};

export const Tabs = ({ items, ...props }: TabsProps) => {
  return (
    <ExtendedTabs
      {...props}
      classNames={{
        ...props.classNames,
        base: clsx(props.classNames?.base),
        panel: clsx(props.classNames?.panel, "w-full"),
        tab: clsx(props.classNames?.tab),
        tabContent: clsx(
          "font-normal text-default-900 group-data-[selected=true]:font-semibold",
          props.classNames?.tabContent,
        ),
        tabList: clsx(props.classNames?.tabList),
      }}
    >
      {items.map((item) => (
        <Tab key={item.key} title={item.title}>
          {item.content}
        </Tab>
      ))}
    </ExtendedTabs>
  );
};

Tabs.displayName = "RecareUI.Tabs";
